import * as React from 'react';
import { compose } from 'redux';
import { select, take } from 'redux-saga/effects';
import { AdditionalResource } from 'author/src/store/context/types';
import { UserPrivileges } from '../store/session/constants';
import { isNullOrUndefined as isNullOrUndef, isNumericString } from './type/typeUtil';
import { isQuestionMode } from './shared/sharedUtils';

export const composeEnhancers = (process.env.NODE_ENV === 'development'
        && window
        && window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__)
    || compose;

export const userIsEvaluator = (userPrivileges: string[]): boolean => {
    return userPrivileges && userPrivileges.length > 0 && userPrivileges.indexOf(UserPrivileges.ar_evaluate) >= 0;
};

export const userIsPlanner = (userPrivileges: string[]): boolean => {
    return userPrivileges && userPrivileges.length > 0 && userPrivileges.indexOf(UserPrivileges.ar_plan) >= 0;
};

export const existValueInEnum = (type: any, value: any): boolean => {
    return Object.keys(type)
        .filter((k) => Number.isNaN(Number(k)))
        .filter((k) => type[k] === value).length > 0;
};

export const objectEmpty = (obj: Record<string, any>): boolean => Object.keys(obj).length === 0;

export const parseParametersString = (str: string): any => {
    return JSON.parse(str.replace(/\\/g, ''));
};

export const isNullOrUndefined = isNullOrUndef;
export const valueIsNumeric = isNumericString;

export const valueContainsCommaOrDot = (value: string): boolean => /[.,]/.exec(value) !== null;

export const convertToNumber = (value: string): number => {
    const hasCommaOrDot: boolean = valueContainsCommaOrDot(value);
    if (hasCommaOrDot) {
        return parseFloat(value.replace(/,/, '.'));
    }
    return parseInt(value, 10);
};

export const containsValidContentBetweenHtml = (content: string): boolean => (
    /<(\w+)[^>]*>((?!<(\w+)[^>]*>|<\/(\w+)[^>]*>)(.+))<\/(\w+)[^>]*>/.exec(content) !== null);

export const getArraysDifference = (a: Array<any>|Set<any>, b: Array<any>|Set<any>) => {
    // since sets are accepted we need this
    const arr1 = [...a];
    const arr2 = [...b];
    return [...arr1].filter((x) => !arr2.includes(x))
        .concat(arr2.filter((x) => !arr1.includes(x)));
};

export function* waitFor(selector: any) {
    if (yield select(selector)) return;

    while (true) {
        yield take('*');
        const obj = yield select(selector);
        if (obj) return;
    }
}

export function usePrevious(value: any) {
    const ref = React.useRef();
    React.useEffect(() => {
        ref.current = value;
    }, [value]);
    return ref.current;
}

export const countDecimals = (value: number): number => {
    if (Math.floor(value) === value) return 0;
    return value.toString().split('.')[1]?.length || 0;
};

export const fixNumberDecimals = (value: number, decimals: number): string => {
    if (countDecimals(value) !== decimals) {
        return value.toFixed(decimals);
    }

    return value.toString();
};

export const fixDecimalsWithDashFallback = (value: number, decimals: number) => {
    return (value || value === 0) ? value.toFixed(decimals) : '-';
};

export const isValidJsonString = (jsonString: string) => {
    try {
        JSON.parse(jsonString);
    } catch (e) {
        return false;
    }

    return true;
};

export const isFileCountExceeded = (fileCount: number, fileType: string, additionalResources: AdditionalResource[]): boolean => {
    const resourceCount = additionalResources?.filter((resource) => resource.type === fileType).length;
    return resourceCount === fileCount;
};

export const getEllipsisWithRemainingCount = (text: string, textList: string[]) => {
    const lastElement = text.split(',').pop().trim();
    const renderedIndex = textList.findIndex((textItem) => textItem.trim().includes(lastElement)) + 1;
    const remainingCount = textList.length - renderedIndex;
    return remainingCount > 0 ? `... +${remainingCount}` : '...';
};

export const STIMULUS_FEATURE_ID = 'feature_structureEditor_author_question_edit_only_with_editor_0_0';

export const ADD_FROM_BANK_QUESTION_STIMULUS = 'feature_addStimulusFromBank_overlay';

export const isAddFromBankForStimulusQuestion = (featureId?: string) => {
    return window.marketplaceProperties.ENABLE_ADD_STIMULUS_FROM_BANK
    && isQuestionMode()
    && (featureId === ADD_FROM_BANK_QUESTION_STIMULUS);
};
